import type { CreateFunctionInput } from '@gonfalon/legacy-immutable/immutableUtils';
import type { Member } from '@gonfalon/members/types';
import { msg } from '@gonfalon/messages/msg';
import { useMember } from '@gonfalon/rest-api/hooks/useMember';
import { ProgressBar } from '@launchpad-ui/components';

import { UserAvatar } from '#components/UserAvatar';
import { useProfileEntity } from '#reducers/profile';
import type { Member as ImmutableMember } from '#utils/members/records/Member';
import { createMember } from '#utils/members/records/Member';

import styles from './SelectMembersAndTeams.module.css';

function getFullName(member: Member) {
  return [member.firstName, member.lastName].filter(Boolean).join(' ').trim();
}
function getDisplayName(member: Member) {
  const fullName = getFullName(member);
  return fullName || member.email;
}

export function MemberOption(props: { memberId: string; member?: Member; compact?: boolean }) {
  const profile = useProfileEntity();
  const memberQuery = useMember({ memberId: props.memberId }, { enabled: !props.member });

  const member = props.member ?? memberQuery.data;

  if (member) {
    const fullName = getFullName(member);
    const label = member._id === profile._id ? msg('approvals.reviewerPicker.me') : getDisplayName(member);

    if (props.compact) {
      return (
        <div className="u-flex u-flex-middle u-fs-sm u-mw-100">
          <UserAvatar
            className={styles.memberAvatar}
            size="small"
            member={createMember(member as CreateFunctionInput<ImmutableMember>)}
          />
          <div className="u-o-hidden u-to-ellipsis">{label}</div>
        </div>
      );
    }

    return (
      <span className="u-flex u-flex-row u-flex-middle u-fs-sm">
        <UserAvatar
          className={styles.memberAvatar}
          member={createMember(member as CreateFunctionInput<ImmutableMember>)}
        />
        {fullName && member.email ? (
          <div className="u-o-hidden u-to-ellipsis">
            <div className={`u-fs-sm-even ${styles.memberName}`}>
              {member._id === profile._id ? msg('approvals.reviewerPicker.me') : fullName}
            </div>
            <div className={`u-fs-xs-even ${styles.memberEmail}`}>{member.email}</div>
          </div>
        ) : (
          label && <div className="u-o-hidden u-to-ellipsis">{label}</div>
        )}
      </span>
    );
  }

  if (memberQuery.isPending) {
    return <ProgressBar aria-label={msg('approvals.status.loading')} isIndeterminate />;
  }

  return null;
}
